import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import {
  getCookieConsent,
  setCookieConsent,
  getCookieInfo,
  COOKIE_CATEGORIES,
  DEFAULT_PREFERENCES
} from '../utils/cookieConsent';

const CookieSettings = ({ open, onClose }) => {
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [expanded, setExpanded] = useState(false);
  const cookieInfo = getCookieInfo();

  useEffect(() => {
    if (open) {
      const currentConsent = getCookieConsent();
      if (currentConsent) {
        setPreferences(currentConsent);
      } else {
        setPreferences(DEFAULT_PREFERENCES);
      }
    }
  }, [open]);

  const handleToggleCategory = (category) => {
    if (category === COOKIE_CATEGORIES.NECESSARY) return; // Can't disable necessary cookies

    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      [COOKIE_CATEGORIES.NECESSARY]: true,
      [COOKIE_CATEGORIES.ANALYTICS]: true,
      [COOKIE_CATEGORIES.MARKETING]: true,
      [COOKIE_CATEGORIES.FUNCTIONAL]: true
    };
    setPreferences(allAccepted);
    setCookieConsent(allAccepted);
    onClose();
  };

  const handleSavePreferences = () => {
    setCookieConsent(preferences);
    onClose();
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const renderCookieCategory = (categoryKey, categoryData) => {
    const isChecked = preferences[categoryKey];
    const canToggle = categoryData.canDisable;

    return (
      <Accordion
        key={categoryKey}
        expanded={expanded === categoryKey}
        onChange={handleAccordionChange(categoryKey)}
        sx={{ mb: 1 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
              justifyContent: 'space-between',
              pr: 1
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {categoryData.name}
            </Typography>
            {!canToggle && (
              <Chip
                label="Always Active"
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={isChecked}
                onChange={() => handleToggleCategory(categoryKey)}
                disabled={!canToggle}
                color="primary"
              />
            }
            label=""
            sx={{ m: 0 }}
            onClick={(e) => e.stopPropagation()}
          />
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              {categoryData.description}
            </Typography>
            
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Examples:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {categoryData.examples.map((example, index) => (
                <Chip
                  key={index}
                  label={example}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              ))}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1
        }}
      >
        <Box>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
            Cookie Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your cookie preferences and privacy settings
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" paragraph>
            We use cookies to enhance your experience on our website. You can choose which 
            categories of cookies you'd like to allow. Note that blocking some types of cookies 
            may impact your experience of the site and the services we are able to offer.
          </Typography>
          
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              backgroundColor: 'info.light',
              border: '1px solid',
              borderColor: 'info.main',
              mb: 3
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <InfoIcon color="info" fontSize="small" />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Your Privacy Matters
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              You can change your preferences at any time by clicking the "Cookie Settings" 
              link in our website footer. Your choices will be saved and respected.
            </Typography>
          </Box>
        </Box>

        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Cookie Categories
        </Typography>

        {Object.entries(cookieInfo).map(([categoryKey, categoryData]) => 
          renderCookieCategory(categoryKey, categoryData)
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ minWidth: '100px' }}
        >
          Cancel
        </Button>
        
        <Button
          variant="outlined"
          onClick={handleAcceptAll}
          sx={{ minWidth: '120px' }}
        >
          Accept All
        </Button>
        
        <Button
          variant="contained"
          onClick={handleSavePreferences}
          sx={{ minWidth: '150px', fontWeight: 600 }}
        >
          Save Preferences
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CookieSettings;